<script setup lang="ts">
import { EyeIcon, EyeSlashIcon, CircleCheckIcon, TriangleExclamationIcon } from 'Common/components/elements/icons/Icons'
import { computed, ref, onUnmounted, toRef } from 'vue'
import ImageSkeleton from 'Common/components/elements/images/ImageSkeleton.vue'

const emit = defineEmits(['onClick', 'onLoaded'])

interface PropTypes {
  uuid: string
  reviewUrl?: string
  imageDescription: string
  imageUrl: string
  viewed: boolean
  approved: boolean
  selected: boolean
  borderColor?: string
  infoPostion?: 'bottom' | 'top'
  imageLoading?: 'lazy' | 'eager'
}

const props = withDefaults(defineProps<PropTypes>(), {
  uuid: '',
  reviewUrl: '#',
  imageUrl: '',
  borderColor: 'transparent',
  viewed: false,
  approved: false,
  selected: false,
  infoPostion: 'bottom',
  imageLoading: 'lazy',
})

const isLoaded = ref(false)
const element = ref(null)
const selected = toRef(props, 'selected')

const imageOpacity = computed<string>(() => {
  return isLoaded.value ? 'opacity-100' : 'opacity-10'
})

const positionClass = computed<string>(() => {
  return `${props.infoPostion}-0`
})

const containerClasses = computed<string>(() => {
  return props.selected ? 'border-amber-500' : `border-${props.borderColor}`
})

defineExpose({ element, selected })

const setIsLoaded = () => {
  isLoaded.value = true
  emit('onLoaded')
}

onUnmounted(() => {
  element.value = null
})
</script>

<template>
  <a
    ref="element"
    :href="props.reviewUrl"
    target="_blank"
    class="relative block h-full border-2 rounded-sm overflow-hidden hover:border-sky-400 hover:cursor-pointer no-underline group"
    :class="containerClasses"
    @click.prevent="$emit('onClick', uuid)"
  >
    <template v-if="!isLoaded">
      <ImageSkeleton />
    </template>

    <div class="h-full w-full snap-center">
      <img
        class="object-cover h-full w-full"
        :class="imageOpacity"
        :loading="props.imageLoading ? props.imageLoading : 'lazy'"
        :src="props.imageUrl"
        :alt="props.imageDescription"
        @load="setIsLoaded"
      />
    </div>
    <div
      class="absolute h-fit w-full text-white bg-neutral-900/50 flex items-center justify-between px-1 info-bar"
      :class="positionClass"
    >
      <span class="text-sm">{{ props.imageDescription }}</span>
      <div class="flex items-center justify-end">
        <div class="mr-2">
          <template v-if="props.viewed">
            <EyeIcon />
          </template>
          <template v-else>
            <EyeSlashIcon />
          </template>
        </div>
        <div>
          <template v-if="props.approved">
            <span class="text-green-400">
              <CircleCheckIcon />
            </span>
          </template>
          <template v-else>
            <span class="text-yellow-400">
              <TriangleExclamationIcon />
            </span>
          </template>
        </div>
      </div>
    </div>

    <div class="hidden print:block text-[9px] text-blue-700 font-bold mt-1 text-center italic">Open Review Page</div>
  </a>
</template>

<style scoped>
/* Reset link styles for the web app UI */
a {
  text-decoration: none;
  color: inherit;
}

@media print {
  .info-bar {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    background-color: rgba(23, 23, 23, 0.5) !important;
  }
}
</style>
